import type { LandingPageOverride, LandingPageType } from '../../types/campaign.js';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile.js';
import type { LandingPageTemplate } from '../../types/franchiseProfile.js';

// Polish round 5 (null=inherit): per-location landing URL overrides.
// When a location's FranchiseLocationProfile carries a non-empty value
// in offerLandingPage / saleLandingPage / locationLandingPage, that
// value beats the brand's landingPageTemplate for the matching type.
// Null and whitespace-only values are treated as inherit (defensive
// against legacy data where these used to be empty strings).
type LocationOverridesSubset = Pick<
  FranchiseLocationProfile,
  'offerLandingPage' | 'saleLandingPage' | 'locationLandingPage'
>;

function locationOverrideFor(
  locationProfile: LocationOverridesSubset | null,
  type: 'offer' | 'sale' | 'location',
): string | null {
  if (!locationProfile) return null;
  const raw =
    type === 'offer'
      ? locationProfile.offerLandingPage
      : type === 'sale'
        ? locationProfile.saleLandingPage
        : locationProfile.locationLandingPage;
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// Client-side preview resolver. Takes the Overview's master selection + the
// per-platform override + the brand's templates, returns a display string
// showing what URL would be used before placeholder substitution.
//
// Important: this is an AUTHORING-TIME preview. Actual per-location URL
// resolution (with placeholder substitution + UTM appending from chunk D)
// happens server-side at AccountCampaign snapshot time in P2b.
//
// Returns null when the selection can't resolve (e.g. location type picked
// but brand has no .location template, or the value is empty). Callers
// render a warning in that case; we intentionally do NOT block save -- per
// spec amendment, unresolvable landings are a rollout-grid warning in P2b,
// not an authoring block.

export type ResolvedLandingPage =
  | { status: 'ok'; url: string }
  | { status: 'empty'; reason: string }
  | { status: 'inherit' };

function resolveType(
  type: LandingPageType,
  templates: LandingPageTemplate | null,
  customUrl: string | null,
  locationProfile: LocationOverridesSubset | null,
): ResolvedLandingPage {
  if (type === 'custom') {
    if (customUrl && customUrl.trim().length > 0) {
      return { status: 'ok', url: customUrl };
    }
    return { status: 'empty', reason: 'No custom URL entered yet.' };
  }
  // Polish round 5 (null=inherit): per-location override beats the
  // brand template when set. The override is an already-resolved URL
  // (no placeholder substitution); call sites still pass the result
  // through renderRowTemplate, which is a no-op when the URL has no
  // {x} tokens. Both master and per-platform-override paths flow
  // through here so both benefit without any extra wiring.
  const override = locationOverrideFor(locationProfile, type);
  if (override !== null) {
    return { status: 'ok', url: override };
  }
  if (!templates) {
    return { status: 'empty', reason: 'Brand has no FranchiseProfile configured.' };
  }
  if (type === 'offer') {
    return templates.offer
      ? { status: 'ok', url: templates.offer }
      : { status: 'empty', reason: 'Brand has no offer landing page template.' };
  }
  if (type === 'sale') {
    return templates.sale
      ? { status: 'ok', url: templates.sale }
      : { status: 'empty', reason: 'Brand has no sale landing page template.' };
  }
  // location
  return templates.location
    ? { status: 'ok', url: templates.location }
    : {
        status: 'empty',
        reason: "Brand has no location landing page template. Set FranchiseProfile.landingPageTemplate.location, or pick a different type.",
      };
}

// Resolve the Overview master landing page. Polish round 5
// (null=inherit): the optional `locationProfile` parameter feeds the
// per-location override check inside resolveType. Authoring-time
// previews (CampaignForm, LandingPageOverrideEditor) call this without
// a locationProfile because they're brand-level — the parameter
// defaults to null and the resolver falls straight through to the
// brand template, preserving existing behaviour for those callers.
export function resolveMasterLanding(
  landingPageType: LandingPageType,
  customLandingPageUrl: string | null,
  templates: LandingPageTemplate | null,
  locationProfile: LocationOverridesSubset | null = null,
): ResolvedLandingPage {
  return resolveType(landingPageType, templates, customLandingPageUrl, locationProfile);
}

// Resolve a platform-specific landing page. When override.type === 'inherit',
// returns { status: 'inherit' } so the caller can surface "same as Overview".
export function resolvePlatformLanding(
  override: LandingPageOverride,
  masterType: LandingPageType,
  masterCustomUrl: string | null,
  templates: LandingPageTemplate | null,
  locationProfile: LocationOverridesSubset | null = null,
): ResolvedLandingPage {
  if (override.type === 'inherit') return { status: 'inherit' };
  if (override.type === 'custom') {
    return override.customUrl && override.customUrl.trim().length > 0
      ? { status: 'ok', url: override.customUrl }
      : { status: 'empty', reason: 'No custom URL entered yet.' };
  }
  return resolveType(override.type, templates, null, locationProfile);
}

// Flatten an inherit result by folding back to the master. Used when a UI
// surface needs the effective URL regardless of inherit vs explicit.
export function flattenInherit(
  platform: ResolvedLandingPage,
  master: ResolvedLandingPage,
): ResolvedLandingPage {
  return platform.status === 'inherit' ? master : platform;
}
